import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, Eye, ArrowLeft, Plus, Trash2, GripVertical, Settings, Palette,
  Type, Image, Video, MessageSquare, Users, DollarSign, HelpCircle,
  BarChart3, Globe, ShoppingBag, Layout, Code, Star, Phone, FileText,
  ChevronDown, ChevronUp, EyeOff, ExternalLink, Package, Layers, Upload
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ImageUploader from '../../components/ImageUploader';
import { getImageUrl } from '../../utils/imageUrl';

const SECTION_ICONS = {
  hero: Globe,
  about: Users,
  features: Star,
  products: ShoppingBag,
  gallery: Image,
  testimonials: MessageSquare,
  team: Users,
  pricing: DollarSign,
  contact: Phone,
  faq: HelpCircle,
  cta: Type,
  text: FileText,
  video: Video,
  stats: BarChart3,
  clients: Globe,
  blog: FileText,
  custom_html: Code
};

const SECTION_NAMES = {
  hero: 'Hero Banner',
  about: 'About Section',
  features: 'Features Grid',
  products: 'Product Showcase',
  gallery: 'Image Gallery',
  testimonials: 'Testimonials',
  team: 'Team Members',
  pricing: 'Pricing Tables',
  contact: 'Contact Form',
  faq: 'FAQ Section',
  cta: 'Call to Action',
  text: 'Text Block',
  video: 'Video Section',
  stats: 'Statistics',
  clients: 'Client Logos',
  blog: 'Blog Posts',
  custom_html: 'Custom HTML'
};

const ShopEditor = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [expandedSection, setExpandedSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchShop();
  }, [shopId]);

  const fetchShop = async () => {
    try {
      const res = await api.get(`/admin/shops/${shopId}`);
      setShop(res.data);
    } catch (error) {
      toast.error('Failed to load shop');
      navigate('/admin/shop-builder');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/shops/${shopId}`, {
        name: shop.name,
        description: shop.description,
        colors: shop.colors,
        is_published: shop.is_published,
        meta_title: shop.meta_title,
        meta_description: shop.meta_description
      });
      await api.put(`/admin/shops/${shopId}/sections`, shop.sections);
      toast.success('Shop saved successfully!');
    } catch (error) {
      toast.error('Failed to save shop');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    const newStatus = !shop.is_published;
    setShop({ ...shop, is_published: newStatus });
    try {
      await api.put(`/admin/shops/${shopId}`, { is_published: newStatus });
      toast.success(newStatus ? 'Shop published!' : 'Shop unpublished');
    } catch (error) {
      setShop({ ...shop, is_published: !newStatus });
      toast.error('Failed to update publish status');
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      order: shop.sections.length,
      title: SECTION_NAMES[type],
      content: getDefaultContent(type),
      settings: {},
      is_visible: true
    };
    setShop({
      ...shop,
      sections: [...shop.sections, newSection]
    });
    setShowAddSection(false);
    setExpandedSection(newSection.id);
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'hero':
        return {
          title: 'Welcome to Our Shop',
          subtitle: 'Discover amazing products',
          cta_text: 'Shop Now',
          cta_link: '#products',
          background_image: ''
        };
      case 'about':
        return {
          title: 'About Us',
          text: 'Tell your story here...',
          image: ''
        };
      case 'features':
        return {
          title: 'Our Features',
          items: [
            { icon: '⭐', title: 'Feature 1', description: 'Description here' },
            { icon: '🚀', title: 'Feature 2', description: 'Description here' },
            { icon: '💎', title: 'Feature 3', description: 'Description here' }
          ]
        };
      case 'testimonials':
        return {
          title: 'What Our Customers Say',
          items: [
            { name: 'Customer 1', text: 'Great service!', image: '', rating: 5 }
          ]
        };
      case 'contact':
        return {
          title: 'Contact Us',
          email: '',
          phone: '',
          address: ''
        };
      case 'cta':
        return {
          title: 'Ready to Get Started?',
          subtitle: 'Join thousands of happy customers',
          button_text: 'Get Started',
          button_link: '#'
        };
      default:
        return {};
    }
  };

  const updateSection = (sectionId, updates) => {
    setShop({
      ...shop,
      sections: shop.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    });
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    setShop({
      ...shop,
      sections: shop.sections.filter(s => s.id !== sectionId)
    });
  };

  const moveSection = (fromIndex, toIndex) => {
    const newSections = [...shop.sections];
    const [removed] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, removed);
    // Update order
    newSections.forEach((s, i) => s.order = i);
    setShop({ ...shop, sections: newSections });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveSection(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!shop) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="shop-editor-page">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/shop-builder')}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{shop.name}</h1>
            <p className="text-muted-foreground">/{shop.slug}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <a
            href={`/shop/${shop.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10 flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </a>
          <button
            onClick={handlePublishToggle}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              shop.is_published
                ? 'bg-green-500/20 text-green-500'
                : 'bg-yellow-500/20 text-yellow-500'
            }`}
          >
            {shop.is_published ? (
              <>
                <Globe className="w-4 h-4" /> Published
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4" /> Draft
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {[
          { id: 'sections', label: 'Sections', icon: Layers },
          { id: 'products', label: 'Products', icon: ShoppingBag },
          { id: 'design', label: 'Design', icon: Palette },
          { id: 'settings', label: 'Settings', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          {/* Add Section Button */}
          <button
            onClick={() => setShowAddSection(true)}
            className="w-full p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-primary/50 transition-all flex items-center justify-center gap-2 text-muted-foreground hover:text-primary"
          >
            <Plus className="w-5 h-5" />
            Add Section
          </button>

          {/* Sections List */}
          {shop.sections.sort((a, b) => a.order - b.order).map((section, index) => {
            const Icon = SECTION_ICONS[section.type] || Layout;
            const isExpanded = expandedSection === section.id;

            return (
              <div
                key={section.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`glass-card overflow-hidden ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                {/* Section Header */}
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5"
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                >
                  <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
                  <Icon className="w-5 h-5 text-primary" />
                  <div className="flex-1">
                    <p className="font-semibold">{section.title || SECTION_NAMES[section.type]}</p>
                    <p className="text-xs text-muted-foreground">{section.type}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateSection(section.id, { is_visible: !section.is_visible });
                    }}
                    className={`p-2 rounded-lg ${section.is_visible ? 'text-green-500' : 'text-muted-foreground'}`}
                  >
                    {section.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSection(section.id);
                    }}
                    className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>

                {/* Section Content Editor */}
                {isExpanded && (
                  <div className="p-4 border-t border-white/10 space-y-4">
                    <SectionEditor
                      section={section}
                      shopId={shopId}
                      onUpdate={(updates) => updateSection(section.id, updates)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <ProductsManager shopId={shopId} products={shop.products || []} onUpdate={fetchShop} />
      )}

      {/* Design Tab */}
      {activeTab === 'design' && (
        <DesignEditor shop={shop} onUpdate={(updates) => setShop({ ...shop, ...updates })} />
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <SettingsEditor shop={shop} onUpdate={(updates) => setShop({ ...shop, ...updates })} />
      )}

      {/* Add Section Modal */}
      {showAddSection && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Add Section</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(SECTION_NAMES).map(([type, name]) => {
                const Icon = SECTION_ICONS[type] || Layout;
                return (
                  <button
                    key={type}
                    onClick={() => addSection(type)}
                    className="p-4 border border-white/10 rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                  >
                    <Icon className="w-6 h-6 text-primary mb-2" />
                    <p className="font-semibold">{name}</p>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowAddSection(false)}
              className="mt-4 w-full px-4 py-2 bg-white/5 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Section Editor Component
const SectionEditor = ({ section, shopId, onUpdate }) => {
  const content = section.content || {};

  const updateContent = (key, value) => {
    onUpdate({ content: { ...content, [key]: value } });
  };

  switch (section.type) {
    case 'hero':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Subtitle</label>
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={(e) => updateContent('subtitle', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Button Text</label>
              <input
                type="text"
                value={content.cta_text || ''}
                onChange={(e) => updateContent('cta_text', e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Button Link</label>
              <input
                type="text"
                value={content.cta_link || ''}
                onChange={(e) => updateContent('cta_link', e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
          <ImageUploader
            shopId={shopId}
            category="hero"
            currentImage={content.background_image}
            onImageUploaded={(url) => updateContent('background_image', url)}
            onImageRemoved={() => updateContent('background_image', '')}
            label="Background Image"
          />
        </div>
      );

    case 'about':
    case 'text':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <textarea
              value={content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              rows={4}
            />
          </div>
          <ImageUploader
            shopId={shopId}
            category="section"
            currentImage={content.image}
            onImageUploaded={(url) => updateContent('image', url)}
            onImageRemoved={() => updateContent('image', '')}
            label="Section Image"
          />
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Subtitle</label>
            <input
              type="text"
              value={content.subtitle || ''}
              onChange={(e) => updateContent('subtitle', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Button Text</label>
              <input
                type="text"
                value={content.button_text || ''}
                onChange={(e) => updateContent('button_text', e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Button Link</label>
              <input
                type="text"
                value={content.button_link || ''}
                onChange={(e) => updateContent('button_link', e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
        </div>
      );

    case 'contact':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => updateContent('title', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={content.email || ''}
              onChange={(e) => updateContent('email', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="text"
              value={content.phone || ''}
              onChange={(e) => updateContent('phone', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Address</label>
            <textarea
              value={content.address || ''}
              onChange={(e) => updateContent('address', e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              rows={2}
            />
          </div>
        </div>
      );

    default:
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Section Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            More editing options coming soon for this section type.
          </p>
        </div>
      );
  }
};

// Products Manager Component
const ProductsManager = ({ shopId, products, onUpdate }) => {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    images: [],
    stock: 0
  });

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      await api.post(`/admin/shops/${shopId}/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price),
        sale_price: newProduct.sale_price ? parseFloat(newProduct.sale_price) : null
      });
      toast.success('Product added!');
      setShowAddProduct(false);
      setNewProduct({ name: '', description: '', price: '', sale_price: '', images: [], stock: 0 });
      onUpdate();
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/shops/${shopId}/products/${productId}`);
      toast.success('Product deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Products ({products.length})</h3>
        <button
          onClick={() => setShowAddProduct(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No products yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <div key={product.id} className="glass-card p-4">
              {product.images?.[0] && (
                <img 
                  src={getImageUrl(product.images[0])} 
                  alt={product.name}
                  className="w-full h-32 object-cover rounded-lg mb-3"
                />
              )}
              <h4 className="font-semibold">{product.name}</h4>
              <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
              <div className="flex items-center gap-2 mt-2">
                {product.sale_price ? (
                  <>
                    <span className="text-lg font-bold text-primary">₹{product.sale_price}</span>
                    <span className="text-sm line-through text-muted-foreground">₹{product.price}</span>
                  </>
                ) : (
                  <span className="text-lg font-bold">₹{product.price}</span>
                )}
              </div>
              <button
                onClick={() => handleDeleteProduct(product.id)}
                className="mt-3 w-full px-3 py-1 bg-red-500/10 text-red-500 rounded-lg text-sm"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="glass-card p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Add Product</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price *</label>
                  <input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Sale Price</label>
                  <input
                    type="number"
                    value={newProduct.sale_price}
                    onChange={(e) => setNewProduct({ ...newProduct, sale_price: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Stock</label>
                <input
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({ ...newProduct, stock: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <ImageUploader
                shopId={shopId}
                category="product"
                currentImage={newProduct.images?.[0] || ''}
                onImageUploaded={(url) => setNewProduct({ ...newProduct, images: [url, ...(newProduct.images || []).slice(1)] })}
                onImageRemoved={() => setNewProduct({ ...newProduct, images: newProduct.images?.slice(1) || [] })}
                label="Product Image"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddProduct(false)}
                className="flex-1 px-4 py-2 bg-white/5 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddProduct}
                className="flex-1 btn-primary"
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Design Editor Component
const DesignEditor = ({ shop, onUpdate }) => {
  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Branding</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <ImageUploader
            shopId={shop.id}
            category="logo"
            currentImage={shop.logo || ''}
            onImageUploaded={(url) => onUpdate({ logo: url })}
            onImageRemoved={() => onUpdate({ logo: '' })}
            label="Shop Logo"
          />
          <ImageUploader
            shopId={shop.id}
            category="favicon"
            currentImage={shop.favicon || ''}
            onImageUploaded={(url) => onUpdate({ favicon: url })}
            onImageRemoved={() => onUpdate({ favicon: '' })}
            label="Favicon"
          />
        </div>
      </div>
      
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Colors</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Primary</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={shop.colors?.primary || '#D4AF37'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, primary: e.target.value } })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={shop.colors?.primary || '#D4AF37'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, primary: e.target.value } })}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Secondary</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={shop.colors?.secondary || '#1a1a2e'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, secondary: e.target.value } })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={shop.colors?.secondary || '#1a1a2e'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, secondary: e.target.value } })}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Accent</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={shop.colors?.accent || '#6366f1'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, accent: e.target.value } })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={shop.colors?.accent || '#6366f1'}
                onChange={(e) => onUpdate({ colors: { ...shop.colors, accent: e.target.value } })}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Editor Component
const SettingsEditor = ({ shop, onUpdate }) => {
  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">General</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Shop Name</label>
            <input
              type="text"
              value={shop.name || ''}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={shop.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              rows={3}
            />
          </div>
        </div>
      </div>

      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">SEO</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Meta Title</label>
            <input
              type="text"
              value={shop.meta_title || ''}
              onChange={(e) => onUpdate({ meta_title: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Meta Description</label>
            <textarea
              value={shop.meta_description || ''}
              onChange={(e) => onUpdate({ meta_description: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              rows={2}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopEditor;
